import { ITokenSuccessResponse } from "@auth/types";
import { IJsonWebTokenPayload } from "@auth/server-sdk";
import { AUTH_COOKIES } from "@/constants/auth";
import { DAYS_IN_YEAR } from "libs/utils/misc/src/dates";

import { cookieDomainSettings, cookieDeleteSettings } from "./utils";

type CookieOptions = ReturnType<typeof cookieDomainSettings>;
type CookieDeleteOptions = ReturnType<typeof cookieDeleteSettings>;

export interface SessionStartResult {
  status: number;
  data: {
    status: string;
    message: string;
  };
  cookies: Array<{
    name: string;
    value: string;
    options: CookieOptions;
  }>;
}

export interface SessionRefreshResult {
  status: number;
  data: {
    status: string;
    message: string;
    accessToken?: string;
    expiresIn?: number;
  };
  cookies: Array<{
    name: string;
    value: string;
    options: CookieOptions;
  }>;
  cookiesToDelete: Array<{
    name: string;
    options: CookieDeleteOptions;
  }>;
}

export interface IImpersonationSessionResult {
  status: number;
  data: {
    status: string;
    message: string;
    accessToken?: string;
    expiresIn?: number;
  };
  cookies: Array<{
    name: string;
    value: string;
    options: CookieOptions;
  }>;
}

export interface SessionEndResult {
  status: number;
  data: {
    status: string;
    message: string;
  };
  cookiesToDelete: Array<{
    name: string;
    options: CookieDeleteOptions;
  }>;
}

/**
 * Calculates default max ages for session cookies based on remember me preference
 */
export function getDefaultSessionMaxAges(rememberMe: boolean) {
  return {
    accessToken: 60 * 60, // 1 hour
    refreshToken: rememberMe ? 60 * 60 * 24 * DAYS_IN_YEAR : 60 * 60, // 1 year for remember me, 1 hour otherwise
    customerId: 60 * 60, // 1 hour
  };
}

/**
 * Shared route handler for setting session cookies after successful login
 * @param requestBody - The parsed request body containing tokens and options
 * @param cookieDomain - The cookie domain from environment variable
 * @returns SessionStartResult with cookies data
 */
export function handleSessionStart(
  requestBody: {
    accessToken: string;
    refreshToken: string;
    kkCustomerId?: string;
    accessTokenMaxAge?: number;
    refreshTokenMaxAge?: number;
    kkCustomerIdMaxAge?: number;
    rememberMe: boolean;
  },
  cookieDomain: string,
): SessionStartResult {
  const {
    accessToken,
    refreshToken,
    kkCustomerId,
    accessTokenMaxAge,
    refreshTokenMaxAge,
    kkCustomerIdMaxAge,
    rememberMe,
  } = requestBody;

  if (!accessToken || !refreshToken) {
    return {
      status: 400,
      data: {
        status: "error",
        message: "Access and Refresh tokens are required",
      },
      cookies: [],
    };
  }

  const defaultMaxAges = getDefaultSessionMaxAges(rememberMe);
  const cookies = [
    {
      name: AUTH_COOKIES.ACCESS_TOKEN,
      value: accessToken,
      options: cookieDomainSettings(cookieDomain, undefined, accessTokenMaxAge ?? defaultMaxAges.accessToken),
    },
    {
      name: AUTH_COOKIES.REFRESH_TOKEN,
      value: refreshToken,
      options: cookieDomainSettings(cookieDomain, undefined, refreshTokenMaxAge ?? defaultMaxAges.refreshToken),
    },
  ];

  if (kkCustomerId) {
    cookies.push({
      name: AUTH_COOKIES.kk_customer_id,
      value: kkCustomerId,
      options: cookieDomainSettings(cookieDomain, undefined, kkCustomerIdMaxAge ?? defaultMaxAges.customerId),
    });
  }

  return {
    status: 200,
    data: {
      status: "success",
      message: "Session cookies set successfully",
    },
    cookies,
  };
}

/**
 * Shared route handler for refreshing session tokens
 * @param refreshToken - The refresh token from cookies
 * @param cookieDomain - The cookie domain from environment variable
 * @param getAccessTokenFromRefreshToken - Function to get new access token
 * @param decodePayload - Function to decode JWT payload
 * @param defaultExpiresIn - Default token expiry time in seconds
 * @returns SessionRefreshResult with new access token data
 */
export async function handleSessionRefresh(
  refreshToken: string | undefined,
  cookieDomain: string,
  getAccessTokenFromRefreshToken: (token: string) => Promise<ITokenSuccessResponse | null>,
  decodePayload: (token: string) => IJsonWebTokenPayload | null,
  defaultExpiresIn: number,
): Promise<SessionRefreshResult> {
  if (!refreshToken) {
    return {
      status: 401,
      data: {
        status: "error",
        message: "Missing refresh token",
      },
      cookies: [],
      cookiesToDelete: [],
    };
  }

  try {
    const response = await getAccessTokenFromRefreshToken(refreshToken);

    if (!response?.access_token) {
      return {
        status: 401,
        data: {
          status: "error",
          message: "Failed to refresh token",
        },
        cookies: [],
        cookiesToDelete: [],
      };
    }

    const accessToken = response.access_token;
    const payload = decodePayload(accessToken);
    const kkCustomerId = payload?.old ?? payload?.iold;
    const expiresIn = response.expires_in || defaultExpiresIn;

    const cookies = [
      {
        name: AUTH_COOKIES.ACCESS_TOKEN,
        value: accessToken,
        options: cookieDomainSettings(cookieDomain, undefined, expiresIn),
      },
    ];

    const cookiesToDelete = [];

    if (kkCustomerId) {
      cookies.push({
        name: AUTH_COOKIES.kk_customer_id,
        value: kkCustomerId,
        options: cookieDomainSettings(cookieDomain, undefined, expiresIn),
      });
    } else {
      cookiesToDelete.push({
        name: AUTH_COOKIES.kk_customer_id,
        options: cookieDeleteSettings(AUTH_COOKIES.kk_customer_id, cookieDomain),
      });
    }

    return {
      status: 200,
      data: {
        status: "success",
        message: "Access token refreshed successfully",
        accessToken,
        expiresIn,
      },
      cookies,
      cookiesToDelete,
    };
  } catch (err) {

    console.error("Token refresh error:", err);
    return {
      status: 500,
      data: {
        status: "error",
        message: "Internal server error during token refresh",
      },
      cookies: [],
      cookiesToDelete: [],
    };
  }
}

/**
 * Builds a successful token refresh response with cookies
 * @param accessToken - The new access token
 * @param expiresIn - Token expiry time in seconds
 * @param cookieDomain - Domain for cookies
 * @param kkCustomerId - Optional customer ID from token payload
 * @returns SessionRefreshResult with cookies data
 */
export function buildTokenRefreshResponse(
  accessToken: string,
  expiresIn: number,
  cookieDomain: string,
  kkCustomerId?: string,
): SessionRefreshResult {
  const cookies = [
    {
      name: AUTH_COOKIES.ACCESS_TOKEN,
      value: accessToken,
      options: cookieDomainSettings(cookieDomain, undefined, expiresIn),
    },
  ];

  const cookiesToDelete = [];

  if (kkCustomerId) {
    cookies.push({
      name: AUTH_COOKIES.kk_customer_id,
      value: kkCustomerId,
      options: cookieDomainSettings(cookieDomain, undefined, expiresIn),
    });
  } else {
    cookiesToDelete.push({
      name: AUTH_COOKIES.kk_customer_id,
      options: cookieDeleteSettings(AUTH_COOKIES.kk_customer_id, cookieDomain),
    });
  }

  return {
    status: 200,
    data: {
      status: "success",
      message: "Access token refreshed successfully",
      accessToken,
      expiresIn,
    },
    cookies,
    cookiesToDelete,
  };
}

/**
 * Builds a successful token for impersonation response with cookies
 * @param accessToken - The new access token
 * @param expiresIn - Token expiry time in seconds
 * @param cookieDomain - Domain for cookies
 * @returns SessionRefreshResult with cookies data
 */
export function buildImpersonationTokenResponse(
  accessToken: string,
  expiresIn: number,
  cookieDomain: string,
): IImpersonationSessionResult {
  const cookies = [
    {
      name: AUTH_COOKIES.ACCESS_TOKEN,
      value: accessToken,
      options: cookieDomainSettings(cookieDomain, undefined, expiresIn),
    },
  ];

  return {
    status: 200,
    data: {
      status: "success",
      message: "Impersonation token added successfully",
      accessToken,
      expiresIn,
    },
    cookies,
  };
}

/**
 * Builds a successful session end response with all cookies cleared
 * @param cookieDomain - Domain for cookies
 * @returns SessionEndResult with cookies to delete
 */
export function buildSessionEndResponse(cookieDomain: string): SessionEndResult {
  return {
    status: 200,
    data: {
      status: "success",
      message: "Session cookies cleared successfully",
    },
    cookiesToDelete: [
      {
        name: AUTH_COOKIES.ACCESS_TOKEN,
        options: cookieDeleteSettings(AUTH_COOKIES.ACCESS_TOKEN, cookieDomain),
      },
      {
        name: AUTH_COOKIES.REFRESH_TOKEN,
        options: cookieDeleteSettings(AUTH_COOKIES.REFRESH_TOKEN, cookieDomain),
      },
      {
        name: AUTH_COOKIES.kk_customer_id,
        options: cookieDeleteSettings(AUTH_COOKIES.kk_customer_id, cookieDomain),
      },
    ],
  };
}
